import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PersonalChargersProperties } from '../../interface/end-consumer.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchPersonalChargersHistory } from '../../redux/slices/EndConsumerManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import BatteryLoader from '../battery-loader/BatteryLoader';

interface ChargerSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Chargers side panel component
 * @param properties
 * @returns
 */
function ChargerSidePanel(properties: ChargerSidePanelProperties) {
  const { fetchIdFrom, id } = properties;
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [chargerDetails, setChargerDetails] = useState<PersonalChargersProperties>();
  const { personalChargers, chargerHistory } = useAppSelector((state) => state.endConsumer);

  const chargerInfo = chargerDetails
    ? [
        { label: t('id'), value: chargerDetails._id },
        { label: t('serialNumber'), value: chargerDetails.chargingPointSn },
        { label: t('chargerName'), value: chargerDetails.chargingPointName },
        { label: t('model'), value: chargerDetails.chargePointModel },
        { label: t('vendor'), value: chargerDetails.chargePointVendor },
        { label: t('firmware'), value: chargerDetails.firmwareVersion },
        {
          label: t('maxOutput'),
          value: `${chargerDetails.maximumOutputPower} ${chargerDetails.maximumOutputPowerUnit}`,
        },
        { label: t('connectorType'), value: chargerDetails.connectorType },
        { label: t('available'), value: chargerDetails.isAvailable ? t('yes') : t('no') },
        { label: t('status'), value: chargerDetails.isDeviceOffline ? t('offline') : t('online') },
        { label: t('userCustomId'), value: chargerDetails.userCustomId },
        {
          label: t('createdAt'),
          value: `${ConvertUTCtoDate(chargerDetails.createdAt)} ${ConvertUTCtoHHMM(chargerDetails.createdAt)}`,
        },
        {
          label: t('updatedAt'),
          value: `${ConvertUTCtoDate(chargerDetails.updatedAt)} ${ConvertUTCtoHHMM(chargerDetails.updatedAt)}`,
        },
        {
          label: t('deletedAt'),
          value: chargerDetails.deletedAt ? ConvertUTCtoDate(chargerDetails.deletedAt) : '—',
        },
      ]
    : [];

  // Compute history content before return
  let historyContent;

  if (isLoadingHistory) {
    historyContent = (
      <p className="loader">
        <BatteryLoader />
      </p>
    );
  } else if (chargerHistory && chargerHistory.length > 0) {
    historyContent = chargerHistory.map((history: any) => (
      <div key={history._id} className="history-item">
        <div className="row">
          <span className="label-text">{t('sessionId')}:</span>
          <span className="value-text">{history._id}</span>
        </div>
        <div className="row">
          <span className="label-text">{t('startTime')}:</span>
          <span className="value-text">
            {ConvertUTCtoDate(history.transactionStartsAt)}{' '}
            {ConvertUTCtoHHMM(history.transactionStartsAt)}
          </span>
        </div>
        <div className="row">
          <span className="label-text">{t('endTime')}:</span>
          <span className="value-text">
            {ConvertUTCtoDate(history.transactionEndsAt)}{' '}
            {ConvertUTCtoHHMM(history.transactionEndsAt)}
          </span>
        </div>
        <div className="row">
          <span className="label-text">{t('kwhConsumed')}:</span>
          <span className="value-text">{history.kiloWattConsumed} {t('kwh')}</span>
        </div>
        <div className="row">
          <span className="label-text">{t('sessionDuration')}:</span>
          <span className="value-text">{history.sessionDuration || '0'} {t('mins')}</span>
        </div>
      </div>
    ));
  } else {
    historyContent = <p className="no-data">{t('noHistoryAvailable')}</p>;
  }

  useEffect(() => {
    let chargerId: string | null | undefined;

    // 1. Get chargerId from URL or props
    if (fetchIdFrom === 'queryParams') {
      chargerId = searchParameters.get('chargerId');
    } else if (fetchIdFrom === 'props') {
      chargerId = id;
    }

    if (!chargerId) return;

    // 2. Find charger details from Redux state
    const selectedCharger = personalChargers.find(
      (charger) => charger.chargingPointSn === chargerId,
    );

    setChargerDetails(selectedCharger);

    setIsLoadingHistory(true);

    // 3. Dispatch using real chargerId (not chargerDetails?.chargingPointSn)
    dispatch(fetchPersonalChargersHistory(chargerId)).finally(() => {
      setIsLoadingHistory(false);
    });
  }, [fetchIdFrom, id, searchParameters, personalChargers, dispatch]);
  return (
    <div className="charger-side-pane-wrap">
      <div className="charger-side-pane-container">
        <div className="charger-side-pane-details">
          <p className="label">{t('chargerDetails')}</p>
          <div className="charger-details">
            {chargerInfo.map((item) => (
              <div key={item.label} className="row">
                <span className="label-text">{item.label} :</span>
                <span className="value-text">{item.value || '—'}</span>
              </div>
            ))}

            {/* Charger Rate */}
            {chargerDetails?.chargeRate && (
              <div className="row">
                <span className="label-text">{t('chargerRate')} :</span>
                <span className="value-text">
                  {chargerDetails.chargeRate.value} {chargerDetails.chargeRate.unit}
                </span>
              </div>
            )}

            {/* Connectors */}
            {chargerDetails?.connectors && chargerDetails.connectors.length > 0 ? (
              <div className="connector-section">
                <p className="label">{t('connectors')}</p>
                {chargerDetails.connectors.map((connector, index) => (
                  <div key={index} className="connector-block">
                    <p className="connector-title">
                      {t('connector')} {connector.connectorId}
                    </p>

                    <div className="row">
                      <span className="label-text">{t('status')} :</span>
                      <span className="value-text">{connector.status}</span>
                    </div>

                    <div className="row">
                      <span className="label-text">{t('available')} :</span>
                      <span className="value-text">
                        {connector.isAvailable ? t('yes') : t('no')}
                      </span>
                    </div>

                    <div className="row">
                      <span className="label-text">{t('scheduleEnabled')} :</span>
                      <span className="value-text">
                        {connector.isReservationEnabled ? t('yes') : t('no')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="connector-section">
                <p className="label">{t('connectors')}</p>
                <div className="connector-block">
                  <p className="connector-title">{t('noConnectorsAvailable')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="charger-side-pane-details">
          <p className="label">{t('chargerHistory')}</p>
          <div className="charger-history">{historyContent}</div>
        </div>
      </div>
    </div>
  );
}

export default ChargerSidePanel;
